/**
 * Footer fixo com navegação
 */

import React from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
  Fab,
  Box,
} from '@mui/material';
import {
  Home,
  People,
  Inventory,
  Analytics,
  ShoppingCart,
  PersonAdd,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { SessionUtil } from '@utils';
import { SessionKeys } from '@types';
import { ROUTES } from '@types';
import theme from '@theme';

interface FooterProps {
  tipo?: 'default' | 'cliente' | 'previsao' | 'pedido';
  activeRoute?: string;
  footerType?: 'default' | 'cadastro' | 'carrinho';
}

export const Footer: React.FC<FooterProps> = ({ tipo = 'default' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Carrega total de itens do carrinho do localStorage
  const getTotalItemsCarrinho = (): number => {
    try {
      const carrinhoData = SessionUtil.get<string>(SessionKeys.CARRINHO);
      if (!carrinhoData) return 0;

      // Se saved já é um objeto, não precisa fazer parse
      const carrinho = typeof carrinhoData === 'string' ? JSON.parse(carrinhoData) : carrinhoData;
      if (!carrinho || !carrinho.items) return 0;

      return Object.keys(carrinho.items).length;
    } catch (error) {
      console.error('Erro ao carregar total de itens do carrinho:', error);
      return 0;
    }
  };

  const [totalItems, setTotalItems] = React.useState(getTotalItemsCarrinho());

  // Atualiza contador do carrinho periodicamente
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTotalItems(getTotalItemsCarrinho());
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Determina o valor ativo baseado na rota atual
  const getCurrentValue = (): string => {
    const path = location.pathname;

    if (path === ROUTES.HOME) return 'home';
    if (path.includes('/clientes') || path.includes('/cliente')) return 'clientes';
    if (path.includes('/produtos') || path.includes('/classificacoes')) return 'produtos';
    if (path.includes('/produto-previsao') || path.includes('/classificacoes-previsao')) return 'previsao';
    if (path.includes('/produto-pedido') || path.includes('/classificacoes-pedido')) return 'pedido';
    if (path.includes('/carrinho')) return 'carrinho';
    if (path.includes('/cadastro-cliente')) return 'cadastro';

    return 'home';
  };

  const [value, setValue] = React.useState(getCurrentValue());

  React.useEffect(() => {
    setValue(getCurrentValue());
  }, [location.pathname]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
    const carrinhoTipo = SessionUtil.get<string>(SessionKeys.CARRINHO_TIPO);

    switch (newValue) {
      case 'home':
        navigate(ROUTES.HOME);
        break;
      case 'clientes':
        navigate(ROUTES.CLIENTES);
        break;
      case 'produtos':
        if (carrinhoTipo === 'previsao') {
        navigate(ROUTES.CLASSIFICACAO_PREVISAO);
        } else if (carrinhoTipo === 'pedido') {
          navigate(ROUTES.CLASSIFICACAO_PEDIDO);
        } else {
          navigate(ROUTES.CLASSIFICACAO);
        }
        break;
      case 'previsao':
        // Previsão: Vai direto para classificações de previsão
        navigate(ROUTES.CLASSIFICACAO_PREVISAO);
        break;
      case 'pedido':
        // Pedido: Vai direto para classificações de pedido
        navigate(ROUTES.CLASSIFICACAO_PEDIDO);
        break;
      case 'cadastro':
        navigate(ROUTES.CAD_CLIENTE);
        break;
      default:
        navigate(ROUTES.HOME);
    }
  };

  const handleCarrinhoClick = () => {
    // Determina qual página de carrinho abrir baseado no tipo do carrinho
    const carrinhoTipo = SessionUtil.get<string>(SessionKeys.CARRINHO_TIPO);

    if (carrinhoTipo === 'previsao') {
      navigate(ROUTES.CARRINHO_PREVISAO);
    } else if (carrinhoTipo === 'pedido') {
      navigate(ROUTES.CARRINHO_PEDIDO);
    } else {
      // Fallback
      navigate(ROUTES.CARRINHO);
    }
  };

  // Determina se deve mostrar Previsão ou Pedido usando SessionUtil
  const showPrevisao = SessionUtil.canMakePrevisao();
  const showPedido = SessionUtil.canMakePedido();

  // Footer para página de clientes (com botão de cadastro)
  if (tipo === 'cliente') {
    return (
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: theme.palette.elite.main
        }}
        elevation={3}
      >
        <BottomNavigation
          value={value}
          onChange={handleChange}
          showLabels
          sx={{bgcolor: theme.palette.elite.main}}
        >
          <BottomNavigationAction
            label="Início"
            value="home"
            icon={<Home />}
            sx={{
              color: theme.palette.elite.contrastText,
              '&.Mui-selected': {
                color: theme.palette.elite.light
              }
            }}
          />
          <BottomNavigationAction
            label="Clientes"
            value="clientes"
            icon={<People />}
            sx={{
              color: theme.palette.elite.contrastText,
              '&.Mui-selected': {
                color: theme.palette.elite.light
              }
            }}
          />
          <BottomNavigationAction
            label="Produtos"
            value="produtos"
            icon={<Inventory />}
            sx={{
              color: theme.palette.elite.contrastText,
              '&.Mui-selected': {
                color: theme.palette.elite.light
              }
            }}
          />
          <BottomNavigationAction
            label="Cadastrar"
            value="cadastro"
            icon={<PersonAdd />}
            sx={{
              color: theme.palette.elite.contrastText,
              '&.Mui-selected': {
                color: theme.palette.elite.light
              }
            }}
          />
        </BottomNavigation>
      </Paper>
    );
  }

  // Footer padrão com carrinho FAB central
  return (
    <Box sx={{ position: 'relative' }}>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000
        }}
        elevation={3}
      >
        <BottomNavigation
          sx={{bgcolor: theme.palette.elite.main}}
          value={value}
          onChange={handleChange}
          showLabels>
          {/* Ordem fixa: Início | Clientes | (CARRINHO) | Previsão/Pedido | Produtos */}

          {/* 1. Início - SEMPRE visível */}
          <BottomNavigationAction
            label="Início"
            value="home"
            icon={<Home />}
            sx={{
              color: theme.palette.elite.contrastText,
              '&.Mui-selected': {
                color: theme.palette.elite.light
              }
            }}
          />

          {/* 2. Clientes - SEMPRE visível */}
          <BottomNavigationAction
            label="Clientes"
            value="clientes"
            icon={<People />}
            sx={{
              color: theme.palette.elite.contrastText,
              '&.Mui-selected': {
                color: theme.palette.elite.light
              }
            }}
          />

          {/* 3. Espaço para o FAB do carrinho quando houver itens */}
          {totalItems > 0 && (
            <BottomNavigationAction
              label=""
              value=""
              disabled
              sx={{
                minWidth: 80,
                opacity: 0,
                pointerEvents: 'none',
                '&.Mui-disabled': {
                  opacity: 0
                }
              }}
            />
          )}

          {/* 4. Previsão - Se indPedido = 0 AND indPrevisao <> 0 */}
          {showPrevisao && (
            <BottomNavigationAction
              label="Previsão"
              value="previsao"
              icon={<Analytics />}
              sx={{
                color: theme.palette.elite.contrastText,
                '&.Mui-selected': {
                  color: theme.palette.elite.light
                }
              }}
            />
          )}

          {/* 5. Pedido - Se indPedido <> 0 AND indPrevisao = 0 */}
          {showPedido && (
            <BottomNavigationAction
              label="Pedido"
              value="pedido"
              icon={<ShoppingCart />}
              sx={{
                color: theme.palette.elite.contrastText,
                '&.Mui-selected': {
                  color: theme.palette.elite.light
                }
              }}
            />
          )}

          {/* 6. Produtos - SEMPRE visível como último item */}
          <BottomNavigationAction
            label="Produtos"
            value="produtos"
            icon={<Inventory />}
            sx={{
              color: theme.palette.elite.contrastText,
              '&.Mui-selected': {
                color: theme.palette.elite.light
              }
            }}
          />
        </BottomNavigation>
      </Paper>

      {/* FAB do Carrinho - Visível quando houver itens */}
      {totalItems > 0 && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 28,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            width: 64,
            height: 64,
          }}
          onClick={handleCarrinhoClick}
        >
          <Badge badgeContent={totalItems} color="error">
            <ShoppingCart sx={{ fontSize: 32 }} />
          </Badge>
        </Fab>
      )}
    </Box>
  );
};

export default Footer;
